import React, { useEffect, useMemo, useRef, useState } from "react";
import { ShieldCheck, ShieldAlert, ShieldX, X, Minus, Square, Lock, Trash2, Play } from "lucide-react";
import { SCAN_STEPS, generateFakeFilename, randomPeer, fakeScanStatus, fakeThreats } from "@/lib/fakeData";

/**
 * Fake virus-scanning modal styled as a 2000s IE popup window.
 * Outcome depends on each track's deterministic scan status:
 *   - clean       → 0 threats, Play enabled
 *   - unverified  → 60% clean, else 1–3 fake threats
 *   - suspicious  → 2–5 fake threats
 * When threats are found the user can still "Play Anyway" (wink) or Quarantine.
 */
export default function VirusScanModal({ track, onComplete, onCancel, onQuarantine }) {
  const [progress, setProgress] = useState(0);
  const [stepIdx, setStepIdx] = useState(0);
  const [finished, setFinished] = useState(false);
  const peerRef = useRef(randomPeer());
  const filename = track ? generateFakeFilename(track) : "";

  const status = track ? fakeScanStatus(track) : "clean";
  const threats = useMemo(() => (track ? fakeThreats(track, status) : []), [track, status]);
  const isInfected = threats.length > 0;

  useEffect(() => {
    setProgress(0);
    setStepIdx(0);
    setFinished(false);
    peerRef.current = randomPeer();

    const start = Date.now();
    const duration = 2800;

    const tick = setInterval(() => {
      const elapsed = Date.now() - start;
      const pct = Math.min(100, (elapsed / duration) * 100);
      setProgress(pct);
      setStepIdx(Math.min(SCAN_STEPS.length - 1, Math.floor((elapsed / duration) * SCAN_STEPS.length)));
      if (elapsed >= duration) {
        clearInterval(tick);
        setFinished(true);
      }
    }, 90);

    return () => clearInterval(tick);
  }, [track?.id]);

  if (!track) return null;

  // Title-bar / accent colors shift on infection
  const titleVariant = !finished ? "xp-titlebar-blue" : isInfected ? "xp-titlebar-red" : "xp-titlebar-green";
  const shieldIcon = !finished
    ? <ShieldAlert size={36} className="text-[#FBC02D] animate-pulse" />
    : isInfected
      ? <ShieldX size={36} className="text-[#E81123]" />
      : <ShieldCheck size={36} className="text-[#1f8020]" />;

  const headerText = !finished
    ? "Scanning downloaded file..."
    : isInfected
      ? `!! ${threats.length} THREAT${threats.length === 1 ? "" : "S"} DETECTED — proceed with caution`
      : "No malware detected. Ready to rock.";

  return (
    <div
      data-testid="virus-scan-modal"
      className="fixed inset-0 z-[80] flex items-center justify-center"
      style={{ background: "rgba(0,0,0,0.35)" }}
    >
      <div className="w-[560px] ie-chrome overflow-hidden flex flex-col">
        {/* Title bar */}
        <div className={`${titleVariant} h-[26px] flex items-center justify-between px-2`}>
          <div className="flex items-center gap-2 text-[12px] font-bold">
            <ShieldCheck size={13} />
            LemonShield 4.2 — File Safety Check
          </div>
          <div className="flex items-center gap-[2px]">
            <WinBtn color="blue"><Minus size={9} strokeWidth={4} /></WinBtn>
            <WinBtn color="blue"><Square size={8} strokeWidth={3} /></WinBtn>
            <WinBtn color="red" onClick={onCancel} data-testid="scan-close"><X size={10} strokeWidth={4} /></WinBtn>
          </div>
        </div>

        {/* Fake menu bar */}
        <div className="h-[20px] flex items-center px-2 gap-3 text-[11px] border-b border-[#9C9A7E] bg-[#ECE9D8]">
          <span><u>F</u>ile</span>
          <span><u>E</u>dit</span>
          <span><u>V</u>iew</span>
          <span><u>T</u>ools</span>
          <span><u>H</u>elp</span>
        </div>

        {/* Address bar */}
        <div className="flex items-center gap-1.5 px-2 py-1 border-b border-[#9C9A7E] bg-[#ECE9D8]">
          <span className="text-[11px] text-[#555]">Address:</span>
          <div className="flex-1 bevel-in bg-white px-2 py-[2px] flex items-center gap-1">
            <Lock size={10} className={finished && isInfected ? "text-[#E81123]" : "text-[#1F8020]"} />
            <span className="font-mono text-[11px] text-[#003c9e] truncate">
              lemonshield://scan/{track.id}
            </span>
          </div>
          <button className="bevel-btn text-[11px] px-3 py-0.5 font-bold">Go</button>
        </div>

        {/* Body */}
        <div className="p-4 bg-white scan-modal-body">
          <div className="flex items-start gap-3">
            <div className={`w-14 h-14 flex items-center justify-center bevel-out shrink-0 ${finished && isInfected ? "bg-[#FFEBEE]" : "bg-white"}`}>
              {shieldIcon}
            </div>
            <div className="min-w-0 flex-1">
              <div className={`text-[13px] font-bold mb-1 ${finished && isInfected ? "text-[#8B0A14]" : "text-[#222]"}`}>
                {headerText}
              </div>
              <div className="text-[11px] font-mono text-[#003c9e] truncate" title={filename}>
                {filename}
              </div>
              <div className="text-[10px] text-[#555] mt-0.5">
                Downloading from peer:&nbsp;
                <span className="font-mono text-[#1F8020]">{peerRef.current}</span>
              </div>
            </div>
          </div>

          <div className="xp-progress my-3">
            <div className="xp-progress-fill" style={{ width: `${progress}%` }} />
          </div>

          <div className="text-[11px] font-mono min-h-[18px] flex items-start gap-2">
            {!finished && (
              <>
                <span className="led-green pulse-green shrink-0 mt-0.5" />
                <span className="truncate text-[#222]">{SCAN_STEPS[stepIdx]}</span>
              </>
            )}

            {finished && !isInfected && (
              <div className="flex flex-col gap-1 w-full">
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-[11px]">
                  <RowKV label="Threats found" value="0" good />
                  <RowKV label="Signature" value="OK" good />
                  <RowKV label="Bitrate integrity" value="VERIFIED" good />
                  <RowKV label="Certified banger" value="YES" good />
                  <RowKV label="Encryption" value="None (safe)" good />
                  <RowKV label="Trojan.Napster.C" value="NOT FOUND" good />
                </div>
                <div className="text-[12px] text-[#1f8020] font-bold mt-1 flex items-center gap-1">
                  <ShieldCheck size={13} /> No malware. Just bangers.
                </div>
              </div>
            )}

            {finished && isInfected && (
              <div className="flex flex-col gap-1 w-full" data-testid="scan-threats">
                <div className="bevel-in bg-[#FFEBEE] p-2">
                  <div className="text-[11px] text-[#8B0A14] font-bold mb-1 flex items-center gap-1">
                    <ShieldX size={12} /> LemonShield Verdict:
                    <span className="uppercase ml-1">{status === "suspicious" ? "SUSPICIOUS" : "UNVERIFIED"}</span>
                  </div>
                  <ul className="space-y-0.5 max-h-[90px] overflow-auto xp-scroll">
                    {threats.map((t, i) => (
                      <li key={`${t}-${i}`} className="flex items-center gap-1.5 text-[11px] font-mono text-[#8B0A14]">
                        <ShieldAlert size={11} className="shrink-0 text-[#E81123]" />
                        <span className="truncate">{t}</span>
                        <span className="ml-auto text-[10px] text-[#5B0207]">heuristic</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="text-[11px] text-[#555] italic mt-0.5">
                  Tip: "Quarantine" to stash it, or "Play Anyway" — at your own risk. (Spoiler: it's still just an iTunes preview.)
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-3 flex-wrap">
            <button
              data-testid="scan-cancel"
              onClick={onCancel}
              className="bevel-btn px-4 py-1 text-[12px] min-w-[80px]"
            >
              Cancel
            </button>
            {finished && isInfected && (
              <button
                data-testid="scan-quarantine"
                onClick={() => onQuarantine ? onQuarantine(track, threats) : onCancel()}
                className="bevel-btn-red px-4 py-1 text-[12px] font-bold flex items-center gap-1"
              >
                <Trash2 size={12} /> Quarantine
              </button>
            )}
            <button
              data-testid="scan-ok"
              onClick={() => finished && onComplete(track, { status, threats })}
              disabled={!finished}
              className={`px-5 py-1 text-[12px] min-w-[110px] font-bold flex items-center justify-center gap-1 ${
                !finished
                  ? "bevel-btn opacity-60 cursor-wait"
                  : isInfected
                    ? "bevel-btn-lemon"
                    : "bevel-btn-green"
              }`}
            >
              {!finished ? "Please wait..." : (
                <>
                  <Play size={12} />
                  {isInfected ? "Play Anyway" : "Play Now"}
                </>
              )}
            </button>
          </div>
        </div>

        {/* IE-style status bar */}
        <div
          className="px-2 py-1 flex items-center justify-between text-[11px] border-t-2 border-[#808080] scan-modal-status"
          style={{ background: "linear-gradient(to bottom, #ECE9D8, #D4D0BF)" }}
        >
          <div className="flex items-center gap-2">
            {!finished && (<><span className="led-yellow" /><span>Scanning... please do not close this window.</span></>)}
            {finished && !isInfected && (<><span className="led-green pulse-green" /><span>Done. File is safe.</span></>)}
            {finished && isInfected && (<><span className="led-red" /><span className="text-[#8B0A14] font-bold">Done. {threats.length} issue{threats.length === 1 ? "" : "s"} found.</span></>)}
          </div>
          <div className="flex items-center gap-1 text-[10px] text-[#555]">
            <Lock size={10} /> Internet | LemonShield™ Zone
          </div>
        </div>
      </div>
    </div>
  );
}

function RowKV({ label, value, good }) {
  return (
    <div className="flex items-center justify-between text-[11px]">
      <span className="text-[#555]">{label}:</span>
      <span className={`font-mono ${good ? "text-[#1F8020] font-bold" : "text-[#222]"}`}>{value}</span>
    </div>
  );
}

function WinBtn({ color, children, ...props }) {
  const colors = color === "red"
    ? "bg-gradient-to-b from-[#F58D85] via-[#E81123] to-[#8B0A14]"
    : "bg-gradient-to-b from-[#7BB3FF] via-[#247DF6] to-[#003c9e]";
  return (
    <button
      {...props}
      className={`w-[18px] h-[16px] flex items-center justify-center text-white rounded-[2px] ${colors} hover:brightness-110`}
    >
      {children}
    </button>
  );
}
